import { createSlice } from "@reduxjs/toolkit";
import {
  cancelCrmImportAction,
  crmImportRowUpserted,
  deleteCrmImportAction,
  estimateCrmPushAction,
  fetchCrmBusinessContextAction,
  fetchCrmBusinessesAction,
  fetchCrmsAction,
  fetchCrmWorkspacesAction,
  getCrmImportAction,
  listCrmImportsAction,
  previewCrmPushAction,
  reviewCrmPushAction,
  searchCrmUsersAction,
  startCrmPushAction,
} from "../../actions/AdminActions/AdminCrmImportAction";
import {
  BusinessContext,
  CrmDefinition,
  ImportRow,
  LookupBusiness,
  LookupUser,
  LookupWorkspace,
  Pagination,
  PushEstimate,
  PushPreview,
  ReviewResponse,
} from "../../../components/Admin/AdminCrmImport/crmImport.types";

interface CrmImportState {
  crms: CrmDefinition[];
  crmsLoading: boolean;
  users: LookupUser[];
  usersPagination: Pagination | null;
  usersLoading: boolean;
  workspaces: LookupWorkspace[];
  workspacesLoading: boolean;
  businesses: LookupBusiness[];
  businessesLoading: boolean;
  businessContext: BusinessContext | null;
  businessContextLoading: boolean;
  imports: ImportRow[];
  importsPagination: Pagination | null;
  importsRunning: number;
  importsLoading: boolean;
  importsFetchedAt: number | null;
  importsError: string | null;
  importDetail: ImportRow | null;
  pushPreview: PushPreview | null;
  pushPreviewLoading: boolean;
  pushEstimate: PushEstimate | null;
  pushEstimateLoading: boolean;
  pushReview: ReviewResponse | null;
  pushReviewLoading: boolean;
  pushStartLoading: boolean;
}

const initialState: CrmImportState = {
  crms: [],
  crmsLoading: false,
  users: [],
  usersPagination: null,
  usersLoading: false,
  workspaces: [],
  workspacesLoading: false,
  businesses: [],
  businessesLoading: false,
  businessContext: null,
  businessContextLoading: false,
  imports: [],
  importsPagination: null,
  importsRunning: 0,
  importsLoading: false,
  importsFetchedAt: null,
  importsError: null,
  importDetail: null,
  pushPreview: null,
  pushPreviewLoading: false,
  pushEstimate: null,
  pushEstimateLoading: false,
  pushReview: null,
  pushReviewLoading: false,
  pushStartLoading: false,
};

const upsertRow = (state: CrmImportState, row: ImportRow) => {
  const index = state.imports.findIndex((item) => item.id === row.id);
  if (index >= 0) state.imports[index] = row;
  else state.imports.unshift(row);
  if (state.importDetail?.id === row.id) state.importDetail = row;
};

const adminCrmImportSlice = createSlice({
  name: "adminCrmImport",
  initialState,
  reducers: {
    clearCrmWorkspaces: (state) => {
      state.workspaces = [];
    },
    clearCrmBusinesses: (state) => {
      state.businesses = [];
    },
    clearCrmBusinessContext: (state) => {
      state.businessContext = null;
    },
    clearCrmImportDetail: (state) => {
      state.importDetail = null;
    },
    clearCrmPushReview: (state) => {
      state.pushReview = null;
    },
    clearCrmPushEstimate: (state) => {
      state.pushEstimate = null;
    },
    resetCrmPush: (state) => {
      state.pushPreview = null;
      state.pushEstimate = null;
      state.pushReview = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCrmsAction.pending, (state) => {
        state.crmsLoading = true;
      })
      .addCase(fetchCrmsAction.fulfilled, (state, action) => {
        state.crmsLoading = false;
        state.crms = action.payload;
      })
      .addCase(fetchCrmsAction.rejected, (state) => {
        state.crmsLoading = false;
      })
      .addCase(searchCrmUsersAction.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(searchCrmUsersAction.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users;
        state.usersPagination = action.payload.pagination;
      })
      .addCase(searchCrmUsersAction.rejected, (state) => {
        state.usersLoading = false;
      })
      .addCase(fetchCrmWorkspacesAction.pending, (state) => {
        state.workspacesLoading = true;
        state.workspaces = [];
      })
      .addCase(fetchCrmWorkspacesAction.fulfilled, (state, action) => {
        state.workspacesLoading = false;
        state.workspaces = action.payload;
      })
      .addCase(fetchCrmWorkspacesAction.rejected, (state) => {
        state.workspacesLoading = false;
      })
      .addCase(fetchCrmBusinessesAction.pending, (state) => {
        state.businessesLoading = true;
        state.businesses = [];
      })
      .addCase(fetchCrmBusinessesAction.fulfilled, (state, action) => {
        state.businessesLoading = false;
        state.businesses = action.payload;
      })
      .addCase(fetchCrmBusinessesAction.rejected, (state) => {
        state.businessesLoading = false;
      })
      .addCase(fetchCrmBusinessContextAction.pending, (state) => {
        state.businessContextLoading = true;
        state.businessContext = null;
      })
      .addCase(fetchCrmBusinessContextAction.fulfilled, (state, action) => {
        state.businessContextLoading = false;
        state.businessContext = action.payload;
      })
      .addCase(fetchCrmBusinessContextAction.rejected, (state) => {
        state.businessContextLoading = false;
      })
      .addCase(listCrmImportsAction.pending, (state) => {
        state.importsLoading = true;
      })
      .addCase(listCrmImportsAction.fulfilled, (state, action) => {
        state.importsLoading = false;
        state.importsError = null;
        state.imports = action.payload.imports;
        state.importsPagination = action.payload.pagination;
        state.importsRunning = action.payload.summary.running;
        state.importsFetchedAt = action.payload.fetchedAt;
      })
      .addCase(listCrmImportsAction.rejected, (state, action) => {
        state.importsLoading = false;
        state.importsError = action.payload?.message || "Could not load import history";
      })
      .addCase(getCrmImportAction.fulfilled, (state, action) => {
        state.importDetail = action.payload;
        const index = state.imports.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) state.imports[index] = action.payload;
      })
      .addCase(cancelCrmImportAction.fulfilled, (state, action) => {
        upsertRow(state, action.payload);
      })
      .addCase(deleteCrmImportAction.fulfilled, (state, action) => {
        state.imports = state.imports.filter((item) => item.id !== action.payload);
        if (state.importDetail?.id === action.payload) state.importDetail = null;
      })
      .addCase(crmImportRowUpserted, (state, action) => {
        upsertRow(state, action.payload);
      })
      .addCase(previewCrmPushAction.pending, (state) => {
        state.pushPreviewLoading = true;
        state.pushPreview = null;
        state.pushEstimate = null;
        state.pushReview = null;
      })
      .addCase(previewCrmPushAction.fulfilled, (state, action) => {
        state.pushPreviewLoading = false;
        state.pushPreview = action.payload;
      })
      .addCase(previewCrmPushAction.rejected, (state) => {
        state.pushPreviewLoading = false;
      })
      .addCase(estimateCrmPushAction.pending, (state) => {
        state.pushEstimateLoading = true;
      })
      .addCase(estimateCrmPushAction.fulfilled, (state, action) => {
        state.pushEstimateLoading = false;
        state.pushEstimate = action.payload;
      })
      .addCase(estimateCrmPushAction.rejected, (state) => {
        state.pushEstimateLoading = false;
      })
      .addCase(reviewCrmPushAction.pending, (state) => {
        state.pushReviewLoading = true;
        state.pushReview = null;
      })
      .addCase(reviewCrmPushAction.fulfilled, (state, action) => {
        state.pushReviewLoading = false;
        state.pushReview = action.payload;
      })
      .addCase(reviewCrmPushAction.rejected, (state) => {
        state.pushReviewLoading = false;
      })
      .addCase(startCrmPushAction.pending, (state) => {
        state.pushStartLoading = true;
      })
      .addCase(startCrmPushAction.fulfilled, (state) => {
        state.pushStartLoading = false;
        state.pushReview = null;
      })
      .addCase(startCrmPushAction.rejected, (state, action) => {
        state.pushStartLoading = false;
        if (action.payload?.code === "REVIEW_REQUIRED") state.pushReview = null;
      });
  },
});

export const {
  clearCrmWorkspaces,
  clearCrmBusinesses,
  clearCrmBusinessContext,
  clearCrmImportDetail,
  clearCrmPushReview,
  clearCrmPushEstimate,
  resetCrmPush,
} = adminCrmImportSlice.actions;
export default adminCrmImportSlice.reducer;
